import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@verifime/api-definition';
import { useUser } from '../UserContext';

interface Identity {
  id?: string;
}

type MasqueradeSessionState =
  | { status: 'loading' }
  | { status: 'error'; errorDetail?: string }
  | { status: 'not-masquerading' }
  | { status: 'masquerading' };

interface MasqueradeSessionContextValue {
  state: MasqueradeSessionState;
  customerId: string | null;
}

interface MasqueradeSessionProviderProps {
  children: React.ReactNode;
  customerId?: string | null;
}

const MasqueradeSessionContext = createContext<MasqueradeSessionContextValue | undefined>(
  undefined,
);

export const MasqueradeSessionProvider: React.FC<MasqueradeSessionProviderProps> = ({
  children,
  customerId,
}) => {
  const { state: userState } = useUser();
  const [state, setState] = useState<MasqueradeSessionState>({ status: 'loading' });

  useEffect(() => {
    const detectMasqueradeSession = async () => {
      setState({ status: 'loading' });

      // Wait for user to be loaded
      if (userState.status === 'loading') {
        return;
      }

      // If user failed to load, propagate error
      if (userState.status === 'error') {
        setState({
          status: 'error',
          errorDetail: userState.errorDetail,
        });
        return;
      }

      try {
        // If no customerId provided, not masquerading
        if (!customerId) {
          setState({ status: 'not-masquerading' });
          return;
        }

        // Prevent masquerade if user is not admin
        if (!userState.isAdmin) {
          setState({
            status: 'error',
            errorDetail: 'Masquerade session requires admin privileges',
          });
          return;
        }

        // Check if customer ID belongs to admin's own identities
        const customerMe = await api.getV1customerme();

        // Determine if masquerading
        const isOwnIdentity = customerMe.customerList?.some(
          (identity: Identity) => identity.id === customerId,
        );

        // Set state based on whether it's admin's own identity
        if (isOwnIdentity) {
          setState({ status: 'not-masquerading' });
        } else {
          setState({ status: 'masquerading' });
        }
      } catch (err) {
        setState({
          status: 'error',
          errorDetail: err instanceof Error ? err.message : 'An error occurred',
        });
      }
    };

    detectMasqueradeSession();
  }, [customerId, userState]);

  const value: MasqueradeSessionContextValue = {
    state,
    customerId: customerId || null,
  };

  return (
    <MasqueradeSessionContext.Provider value={value}>{children}</MasqueradeSessionContext.Provider>
  );
};

export const useMasqueradeSession = (): MasqueradeSessionContextValue => {
  const context = useContext(MasqueradeSessionContext);
  if (context === undefined) {
    throw new Error('useMasqueradeSession must be used within a MasqueradeSessionProvider');
  }
  return context;
};
